import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { colors } from '../../../constants/colors';
import { WEB_ANALYTICS_COLORS } from '../../../constants/webAnalyticsTheme';

const BROWSER_COLORS = [
  WEB_ANALYTICS_COLORS.browsers,
  WEB_ANALYTICS_COLORS.unique_visitors,
  WEB_ANALYTICS_COLORS.sessions,
  WEB_ANALYTICS_COLORS.time_on_page,
  WEB_ANALYTICS_COLORS.bounce,
  colors.text.light,
];

const BrowserBreakdownChart = ({ data = [], isLoading = false }) => {
  const chartData = data
    .map((item, index) => ({
      name: String(item.browser || 'Unknown'),
      value: item.count || 0,
      percentage: item.percentage != null ? Number(item.percentage) : null,
      color: BROWSER_COLORS[index % BROWSER_COLORS.length],
    }))
    .filter((item) => item.value > 0);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);
  const paddingAngle = chartData.length > 1 ? 2 : 0;

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          color: colors.text.secondary,
        }}
      >
        Loading...
      </div>
    );
  }

  if (!chartData.length || total === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          color: colors.text.secondary,
          textAlign: 'center',
        }}
      >
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>No browser data yet</p>
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          Browser split appears once visitors hit your site
        </p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    const pct =
      row?.percentage != null
        ? Number(row.percentage).toFixed(1)
        : total
          ? ((row.value / total) * 100).toFixed(1)
          : '0';
    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 10,
          padding: '10px 12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ fontWeight: 600, color: colors.text.primary, fontSize: 13 }}>
          {row.name}
        </div>
        <div style={{ color: colors.text.secondary, fontSize: 12, marginTop: 2 }}>
          {row.value.toLocaleString()} views · {pct}%
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 28,
        width: '100%',
        minHeight: 200,
      }}
    >
      <div style={{ width: 160, height: 160, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="88%"
              paddingAngle={paddingAngle}
              dataKey="value"
              stroke="none"
              isAnimationActive={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`browser-${entry.name}-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {chartData.map((item) => {
          const pct =
            item.percentage != null
              ? Math.round(item.percentage)
              : total
                ? Math.round((item.value / total) * 100)
                : 0;
          return (
            <div key={item.name}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 5,
                  fontSize: 13,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontWeight: 600, color: colors.text.primary }}>
                    {item.name}
                  </span>
                </span>
                <span style={{ color: colors.text.secondary, fontWeight: 600 }}>
                  {pct}%
                </span>
              </div>
              <div
                style={{
                  height: 4,
                  borderRadius: 999,
                  backgroundColor: '#F3F4F6',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.max(4, pct)}%`,
                    height: '100%',
                    borderRadius: 999,
                    backgroundColor: item.color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BrowserBreakdownChart;
